// Store Sync
// Synchronize data between Vuex and Google Drive
import { debounce } from 'lodash'
import { diff } from 'deep-object-diff'
import google from "./google"
import router from '../routes'
import { clearDBCache, getCachedDB, setCachedDB } from './database_cache'
import { MD5 } from 'object-hash'

const
  FOLDER_NAME = "Papartisan",
  DATABASE_NAME = "papartisan_database.json",
  DATABASE_MIME = "application/json",
  EMPTY_DATABASE = "{}",
  AUTOSAVE_MIN_WAIT = 3000,
  AUTOSAVE_MAX_WAIT = 20000,
  UNPERSISTABLE_MUTATIONS = [
    "become",
    "logout",
    "resetState",
    "setLoginError",
    "setLoginStatus",
    "appendLoginStatus",
    "addTrackedRequest",
    "clearTrackedRequest",
    "setActiveGame",
    "clearActiveGame",
    "setActiveComponent",
    "clearActiveComponent",
    "setActiveLayer",
    "clearActiveLayer",
    "setActiveItem",
    "setLayerHighlighting",
    "cache",
    "setExportStartedAt",
    "setExportGameStatus",
    "setExportItemStatus",
    "setStatusWindowOpen",
    "createError",
    "incrementUnreadErrorCount",
    "setUnreadErrorCount",
    "clearUnreadErrorCount",
    "setGoogleFonts",
    "setRequestPromise",
  ]

let
  vuex,
  vuexSubscriptionCanceler,
  databaseFileId,
  workingDirectoryId

const
  initialize = async store => {
    vuex = store

    registerSignInListener()

    // Try to restore saved token from localStorage
    const savedToken = localStorage.getItem('papartisan_token')
    const savedExpiry = localStorage.getItem('papartisan_token_expiry')

    if (savedToken && savedExpiry && Date.now() < parseInt(savedExpiry)) {
      try {
        await google.auth.restoreToken(savedToken)
        return signInStatusChange(true)
      } catch(e) {
        localStorage.removeItem('papartisan_token')
        localStorage.removeItem('papartisan_token_expiry')
      }
    }

    return startApplication()
  },

  registerSignInListener = () => {
    google.auth.registerSignInListener(signInStatusChange)
  },

  signInStatusChange = signedIn => {
    if(signedIn) {
      return Promise.all([
        becomeSignedInUser(),
        syncAll()
      ])
    }

    // wipe caches
    unsyncAll()
    // wipe vuex
    vuex.commit("resetState")
    // go home
    router.push({ name: "splash" })
  },

  isSignedIn = () => google.auth.isSignedIn(),

  becomeSignedInUser = async () => {
    const
      { email, name, imageUrl } = await google.auth.getCurrentUserProfile(),
      userRecord = {
        idToken:   email,
        name:      name,
        avatarSrc: imageUrl
      }

    return vuex.dispatch("become", userRecord)
  },

  syncAll = async () => {
    const
      cachedDB = await getCachedDB(),
      cachedMD5 = MD5(cachedDB || {})

    if(cachedDB) {
      const migratedDB = await vuex.dispatch("migrate", { ...cachedDB })
      vuex.commit("resetState", migratedDB)
      await startApplication()
    }

    await initializeDrive()

    const downloadedDBString = await vuex.dispatch("googleDownloadFile", vuex.getters.databaseFileId)
    if(!downloadedDBString) {
      throw new Error(`Failed to load database: ${vuex.getters.databaseFileId}`)
    }

    const
      downloadedDB = JSON.parse(downloadedDBString),
      downloadedMD5 = MD5(downloadedDB || {})

    if(!downloadedDB) {
      throw new Error(`Downloaded database didn't JSON parse: ${downloadedDBString}`)
    }

    if(cachedMD5 !== downloadedMD5) {
      cachedDB && console.warn("Database from Google Drive didn't match the local cache:", diff(cachedDB, downloadedDB))
      const { activeGame, activeComponent, activeItem, activeLayer } = vuex.getters

      const migratedState = await vuex.dispatch("migrate", downloadedDB)
      vuex.commit("resetState", migratedState)

      activeGame && vuex.dispatch("setActiveGame", activeGame.id)
      activeComponent && vuex.dispatch("setActiveComponent", activeComponent.id)
      activeItem && vuex.dispatch("setActiveItem", activeItem)
      activeLayer && vuex.dispatch("setActiveLayer", activeLayer)

      setCachedDB(vuex.getters.databaseState)
    }

    if(!vuex.getters.workingFolder) {
      await vuex.dispatch("refreshRootFolderIndex")
    }

    await startApplication()

    return syncVuexToDrive()
  },

  unsyncAll = () => {
    clearDBCache()
    vuexSubscriptionCanceler()
    vuexSubscriptionCanceler = null
  },

  initializeDrive = async () => {
    const
      gd = google.drive,
      maybeFolderIds = await gd.findFolders(FOLDER_NAME),
      folderIds = (maybeFolderIds.length > 0)
        ? maybeFolderIds
        : [ await gd.createFolder(FOLDER_NAME) ],
      [ maybeDatabaseId, maybeParentId ] = await gd.findFile(DATABASE_NAME, DATABASE_MIME, folderIds)

    if(!maybeDatabaseId) {
      workingDirectoryId = folderIds[0],
      databaseFileId = await gd.createFile(DATABASE_NAME, DATABASE_MIME, workingDirectoryId, EMPTY_DATABASE)
    } else {
      workingDirectoryId = maybeParentId
      databaseFileId = maybeDatabaseId
    }

    return vuex.dispatch("prepareRootItems", {
      workingDirectory: { id: workingDirectoryId, name: FOLDER_NAME },
      databaseFile: { id: databaseFileId, name: DATABASE_NAME }
    })
  },

  autoSave = debounce(async () => {
    await vuex.dispatch("saveToDrive")
    await setCachedDB(vuex.getters.databaseState)
  }, AUTOSAVE_MIN_WAIT, { maxWait: AUTOSAVE_MAX_WAIT }),

  syncVuexToDrive = () => {
    if(vuexSubscriptionCanceler) {
      throw new Error("Already subscribed to Vuex mutations!")
    }

    vuexSubscriptionCanceler = vuex.subscribe(({ type }) => {
      if(!UNPERSISTABLE_MUTATIONS.includes(type)) {
        autoSave()
      }
    })
  },

  startApplication = () => vuex.dispatch("setStoreReady")

export default { initialize }